import { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const TutorialOverlay = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showTutorial, setShowTutorial] = useState(false);
  const { user, setUser } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    // Check if user is coming from ProfileSetup by looking for URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const fromSetup = urlParams.get("fromSetup");

    // Check if tutorial was already completed
    const checkTutorialStatus = async () => {
      if (user?.uid) {
        const userDoc = await getDoc(doc(db, "students", user.uid));
        const userData = userDoc.data();

        if (
          fromSetup === "true" ||
          (userData && !userData.hasCompletedTutorial)
        ) {
          setShowTutorial(true);
        }
      }
    };

    checkTutorialStatus();
  }, [user]);

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const handleSkip = async () => {
    completeTutorial();
  };

  const completeTutorial = async () => {
    setShowTutorial(false);

    // Update user record in Firestore to mark tutorial as completed
    if (user?.uid) {
      try {
        await updateDoc(doc(db, "students", user.uid), {
          hasCompletedTutorial: true,
        });

        // Update local user state
        setUser((prev) => ({
          ...prev,
          hasCompletedTutorial: true,
        }));
      } catch (error) {
        console.error("Error updating tutorial status:", error);
      }
    }

    // Remove the URL parameter
    const url = new URL(window.location);
    url.searchParams.delete("fromSetup");
    window.history.replaceState({}, "", url);
  };

  if (!showTutorial) return null;

  let tooltipPosition = {};
  let tooltipContent = {};

  // Position and content based on current step
  if (currentStep === 1) {
    tooltipPosition = {
      top: "194px",
      left: "420px",
      transform: "translateX(-50%)",
    };
    tooltipContent = {
      title: t("tutorial.step1.title", "Explore Certified Instructors"),
      description: t(
        "tutorial.step1.description",
        "Book live 1:1 language classes with certified instructors to bring your language learning to the next level.",
      ),
    };
  } else if (currentStep === 2) {
    tooltipPosition = {
      top: "125px",
      left: "415px",
      transform: "translateX(-50%)",
    };
    tooltipContent = {
      title: t("tutorial.step2.title", "AI Tutor"),
      description: t(
        "tutorial.step2.description",
        "Practice 24/7 with our AI language SuperTutor. Ask SuperTutor for language translations and grammar questions. You can also have a practice conversation in the language you are learning!",
      ),
    };
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      <div
        className="pointer-events-auto absolute z-50 ml-[-24px]"
        style={tooltipPosition}
      >
        <div className="w-[320px] rounded-2xl bg-[#042F0C] p-4 text-white">
          <h3 className="mb-1 text-base font-semibold">
            {tooltipContent.title}
          </h3>
          <p className="mb-6 text-sm font-normal text-[#DBFDDF]">
            {tooltipContent.description}
          </p>
          <div className="flex items-center justify-between">
            <button onClick={handleSkip} className="text-white hover:underline">
              {t("tutorial.buttons.skip", "Skip")}
            </button>{" "}
            <button
              onClick={handleNext}
              className="rounded-full px-4 py-1 font-bold text-white hover:bg-opacity-90"
            >
              {currentStep === 2
                ? t("tutorial.buttons.done", "Done (4/4)")
                : t("tutorial.buttons.next", "Next (3/4)")}
            </button>
          </div>
        </div>
        {/* Moved the notch to the left side of the popup */}
        <div className="absolute -left-2 top-1/2 -translate-y-1/2 transform">
          <div className="h-0 w-0 border-b-8 border-r-8 border-t-8 border-transparent border-r-[#042F0C]" />
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
